// Org-scoped Firestore helper factory. Wraps the `window.firestore*`
// globals set up by firebase-init.ts into a tidy {get, set, subscribe}
// surface keyed to `organizations/{orgId}/data/{key}` documents.
//
// Extracted from App.tsx during M2.

import {
  reportConnectionError,
  reportConnectionHealthy,
} from './connectionStatus'

export function createDb(orgId: string) {
  return {
    get: async (key: string) => {
      try {
        const docRef = window.firestoreDoc(window.firebaseDb, 'organizations', orgId, 'data', key)
        const docSnap = await window.firestoreGetDoc(docRef)
        return docSnap.exists() ? docSnap.data().value : null
      } catch (e) {
        console.error('db.get error:', key, e)
        reportConnectionError(`get:${key}`, e)
        return null
      }
    },
    /**
     * Definitive existence check. Unlike `get` (which returns null for BOTH a
     * missing doc AND a failed read), this returns true/false only on a
     * successful read and THROWS on any read error — so callers can tell
     * "genuinely missing" apart from "couldn't read". Use for seed-if-missing
     * logic that must never overwrite real data on a transient read failure.
     */
    exists: async (key: string): Promise<boolean> => {
      const docRef = window.firestoreDoc(window.firebaseDb, 'organizations', orgId, 'data', key)
      const docSnap = await window.firestoreGetDoc(docRef)
      return docSnap.exists()
    },
    set: async (key: string, value: any) => {
      try {
        await window.firestoreSetDoc(
          window.firestoreDoc(window.firebaseDb, 'organizations', orgId, 'data', key),
          { value, updatedAt: new Date().toISOString() },
        )
        console.log('db.set success:', key)
        return true
      } catch (e: any) {
        console.error('db.set error:', key, e)
        alert(`Error saving: ${e.message}`)
        return false
      }
    },
    /**
     * Transactional read-modify-write for the org-scoped data doc at
     * `key`. `mutate` receives the current `value` (or null if the doc
     * doesn't exist yet) and must return the new value to write.
     *
     * Backed by a Firestore transaction so concurrent writes get
     * detected and retried with fresh data — use this anywhere two
     * clients might write the same doc at the same time (the meetings
     * list is the canonical example: two stake admins editing
     * different meetings simultaneously must not clobber each other).
     *
     * `mutate` may be called more than once if the transaction
     * retries; keep it pure and side-effect-free.
     */
    patch: async (key: string, mutate: (currentValue: any) => any) => {
      try {
        await window.firestoreRunTransaction(window.firebaseDb, async (txn: any) => {
          const ref = window.firestoreDoc(
            window.firebaseDb,
            'organizations',
            orgId,
            'data',
            key,
          )
          const snap = await txn.get(ref)
          const currentValue = snap.exists() ? snap.data()?.value : null
          const newValue = mutate(currentValue)
          txn.set(ref, { value: newValue, updatedAt: new Date().toISOString() })
        })
        return true
      } catch (e: any) {
        console.error('db.patch error:', key, e)
        alert(`Error saving: ${e.message}`)
        return false
      }
    },
    subscribe: (key: string, callback: (value: any) => void) => {
      const docRef = window.firestoreDoc(window.firebaseDb, 'organizations', orgId, 'data', key)
      return window.firestoreOnSnapshot(
        docRef,
        (doc: any) => {
          // A live snapshot means the connection is healthy — clear any
          // outstanding warning before delivering the data.
          reportConnectionHealthy()
          if (doc.exists()) callback(doc.data().value)
        },
        (e: any) => {
          // onSnapshot stops delivering after it errors (expired token,
          // revoked access, permanent network failure). Surface it so the
          // user knows their data has gone stale rather than failing silent.
          console.error('db.subscribe error:', key, e)
          reportConnectionError(`subscribe:${key}`, e)
        },
      )
    },

    // ─── Per-record subcollection helpers ──────────────────────────────
    // Operate on organizations/{orgId}/{collName}/{docId} — for data
    // extracted out of the monolithic blobs so Firestore rules can gate
    // reads per record (e.g. stakeInterviews). Distinct from the data/{key}
    // helpers above.
    subscribeCollection: (
      collName: string,
      callback: (rows: any[]) => void,
      onError?: (e: any) => void,
    ) => {
      const collRef = window.firestoreCollection(window.firebaseDb, 'organizations', orgId, collName)
      return window.firestoreOnSnapshot(
        collRef,
        (snap: any) => {
          reportConnectionHealthy()
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })))
        },
        (e: any) => {
          // Permission-denied is EXPECTED for users without read access to this
          // collection — let the caller treat it as empty instead of surfacing
          // a connection error.
          if (onError) onError(e)
          else {
            console.error('db.subscribeCollection error:', collName, e)
            reportConnectionError(`subscribeCollection:${collName}`, e)
          }
        },
      )
    },
    setDocIn: async (collName: string, docId: string, value: any) => {
      try {
        await window.firestoreSetDoc(
          window.firestoreDoc(window.firebaseDb, 'organizations', orgId, collName, docId),
          value,
        )
        return true
      } catch (e: any) {
        console.error('db.setDocIn error:', collName, docId, e)
        alert(`Error saving: ${e.message}`)
        return false
      }
    },
    deleteDocIn: async (collName: string, docId: string) => {
      try {
        await window.firestoreDeleteDoc(
          window.firestoreDoc(window.firebaseDb, 'organizations', orgId, collName, docId),
        )
        return true
      } catch (e: any) {
        console.error('db.deleteDocIn error:', collName, docId, e)
        alert(`Error deleting: ${e.message}`)
        return false
      }
    },
    // Subscribe to a filtered collection query. `constraints` is a list of
    // [field, op, value] tuples (e.g. [['assigneeEmail','==',email]]). Used so
    // non-privileged users can read only the action items assigned/shared to
    // them (a whole-collection read would be denied by rules).
    subscribeQuery: (
      collName: string,
      constraints: Array<[string, any, any]>,
      callback: (rows: any[]) => void,
      onError?: (e: any) => void,
    ) => {
      const collRef = window.firestoreCollection(window.firebaseDb, 'organizations', orgId, collName)
      const q = window.firestoreQuery(
        collRef,
        ...constraints.map(([f, op, v]) => window.firestoreWhere(f, op, v)),
      )
      return window.firestoreOnSnapshot(
        q,
        (snap: any) => {
          reportConnectionHealthy()
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })))
        },
        (e: any) => {
          if (onError) onError(e)
          else {
            console.error('db.subscribeQuery error:', collName, e)
            reportConnectionError(`subscribeQuery:${collName}`, e)
          }
        },
      )
    },
    // Partial update (vs. setDocIn's full overwrite). Needed so a group member
    // can flip only their own `completedBy` key — the rules allow a non-edit
    // member to update that field alone but not overwrite the whole doc.
    updateDocIn: async (collName: string, docId: string, partial: any) => {
      try {
        await window.firestoreUpdateDoc(
          window.firestoreDoc(window.firebaseDb, 'organizations', orgId, collName, docId),
          partial,
        )
        return true
      } catch (e: any) {
        console.error('db.updateDocIn error:', collName, docId, e)
        alert(`Error saving: ${e.message}`)
        return false
      }
    },
  }
}
